import React, { Fragment, useCallback } from 'react';

import PageableTable, {
  TableHeaderMobileHidden,
  TableHeaderMobileOnly,
} from 'src/components/pageable-table';
import { Project } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import EmptyResults from 'src/sections/workspace/components/empty-results';
import { useWorkspaceNavActionUrls } from 'src/sections/workspace/components/navigation/use-container-navigation-props';

import ProjectRow from './project-row';
import messages from './projects.i18n';

export type ProjectTableProps = {
  projects: Project[] | undefined;
  isError: boolean;
  isLoading: boolean;
  renderPagination: () => JSX.Element;
  isFiltering: boolean;
};

export const ProjectTable = (props: ProjectTableProps): JSX.Element => {
  const { projects, isError, isLoading, renderPagination, isFiltering } = props;
  const { createProjectUrl } = useWorkspaceNavActionUrls();
  const { formatMessage } = useIntl();

  const renderDesktopHeaders = () => {
    return (
      <tr>
        <TableHeaderMobileHidden scope="col" width="25%">
          {formatMessage(messages.projectRowHeaderTitle)}
        </TableHeaderMobileHidden>
        <TableHeaderMobileHidden scope="col" width="25%">
          {formatMessage(messages.projectRowHeaderKey)}
        </TableHeaderMobileHidden>
        <TableHeaderMobileHidden scope="col" width="50%">
          {formatMessage(messages.projectRowHeaderDescription)}
        </TableHeaderMobileHidden>
      </tr>
    );
  };

  const renderMobileHeaders = () => {
    return (
      <tr>
        <TableHeaderMobileOnly scope="col" width="40%">
          {formatMessage(messages.projectRowHeaderTitle)}
        </TableHeaderMobileOnly>
        <TableHeaderMobileOnly scope="col" width="60%">
          {formatMessage(messages.projectRowHeaderDescription)}
        </TableHeaderMobileOnly>
      </tr>
    );
  };

  const renderHeaders = () => {
    return (
      <Fragment>
        {renderDesktopHeaders()}
        {renderMobileHeaders()}
      </Fragment>
    );
  };

  // Empty states:
  // 1) Current filter does not match any projects or
  // 2) User does not have access to any projects
  const renderEmptyState = useCallback(() => {
    const emptyStateProps = isFiltering
      ? {
          header: messages.noMatchTitle,
          desc: messages.noMatchSubtitle,
          primaryAction: {
            href: window.location.pathname,
            msg: messages.clearAllFilters,
          },
        }
      : {
          header: messages.emptyStateTitle,
          desc: messages.emptyStateSubtitle,
          // "Create a project" button will only show if user has access
          primaryAction: createProjectUrl
            ? {
                href: createProjectUrl,
                msg: messages.createProjectLink,
              }
            : undefined,
        };

    return (
      <tr>
        <td colSpan={3}>
          <EmptyResults {...emptyStateProps} />
        </td>
      </tr>
    );
  }, [isFiltering, createProjectUrl]);

  return (
    <PageableTable
      rows={projects}
      renderHeaders={renderHeaders}
      isError={isError}
      isLoading={isLoading}
      renderPagination={renderPagination}
      renderEmptyState={renderEmptyState}
    >
      {project => <ProjectRow key={project.uuid} project={project} />}
    </PageableTable>
  );
};
